import React from 'react';
import { C } from '../constants/theme';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const btnBase = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
  background: C.white, cursor: 'pointer', transition: 'all .15s',
  color: C.textMuted,
};

function PageBtn({ onClick, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...btnBase,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.background = '#F5F3FC'; e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; } }}
      onMouseLeave={(e) => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}
    >
      {children}
    </button>
  );
}

export function Pagination({ page, totalPages, totalElements, pageSize, onPageChange, onPageSizeChange }) {
  const start = totalElements === 0 ? 0 : page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalElements);

  // Page numbers generate karo
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(0, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 20px', borderTop: `1px solid ${C.border}`,
      background: C.white, flexWrap: 'wrap', gap: 12,
    }}>

      {/* Left — records info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, color: C.textMuted }}>
          Showing <span style={{ fontWeight: 600, color: C.text }}>{start}–{end}</span> of{' '}
          <span style={{ fontWeight: 600, color: C.text }}>{totalElements}</span> records
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            style={{
              border: `1px solid ${C.border}`, borderRadius: 8,
              padding: '4px 10px', fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', background: C.white, color: C.text,
              outline: 'none',
            }}
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Right — page controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>

        {/* First */}
        <PageBtn onClick={() => onPageChange(0)} disabled={page === 0}>
          <ChevronsLeft size={14} strokeWidth={2} />
        </PageBtn>

        {/* Prev */}
        <PageBtn onClick={() => onPageChange(page - 1)} disabled={page === 0}>
          <ChevronLeft size={14} strokeWidth={2} />
        </PageBtn>

        {/* Page numbers */}
        {getPageNumbers().map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            style={{
              width: 32, height: 32, borderRadius: 8, border: 'none',
              cursor: 'pointer', fontSize: 13, fontWeight: p === page ? 700 : 400,
              background: p === page ? C.primary : 'transparent',
              color: p === page ? '#fff' : C.textMuted,
              transition: 'all .15s', fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { if (p !== page) e.currentTarget.style.background = '#F5F3FC'; }}
            onMouseLeave={(e) => { if (p !== page) e.currentTarget.style.background = 'transparent'; }}
          >
            {p + 1}
          </button>
        ))}

        {/* Next */}
        <PageBtn onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}>
          <ChevronRight size={14} strokeWidth={2} />
        </PageBtn>

        {/* Last */}
        <PageBtn onClick={() => onPageChange(totalPages - 1)} disabled={page >= totalPages - 1}>
          <ChevronsRight size={14} strokeWidth={2} />
        </PageBtn>

      </div>
    </div>
  );
}